import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { useMsal, useIsAuthenticated } from "@azure/msal-react";
import { InteractionStatus } from "@azure/msal-browser";
import {
  getAdminMe, listAdminOrgs, listOrgUsers,
  getActingOrgId, setActingOrgId,
  getActingUserId, setActingUserId,
} from "../utils/api";

// Last known admin verdict for THIS browser, so the view-as / act-as controls render
// immediately on load instead of popping in (or never appearing) after the /admin/me probe.
// This is a UI hint ONLY — visibility of a switcher grants nothing. Every impersonated read
// is authorised server-side against the real caller (GLOBAL_ADMIN_* allowlist), so a forged
// cache value buys a non-admin a menu whose every entry 403s.
const ADMIN_HINT_KEY = "admin_hint";

function readAdminHint() {
  try {
    const v = window.localStorage.getItem(ADMIN_HINT_KEY);
    return v === "yes" || v === "no" ? v : "unknown";
  } catch (_) {
    return "unknown";
  }
}

function writeAdminHint(v) {
  try {
    if (v === "yes" || v === "no") window.localStorage.setItem(ADMIN_HINT_KEY, v);
    else window.localStorage.removeItem(ADMIN_HINT_KEY);
  } catch (_) {}
}

// Called on sign-out so a shared browser doesn't carry an admin's UI hint into the next
// person's session.
export function clearAdminHint() {
  writeAdminHint(null);
}

const ImpersonationContext = createContext({
  isAdmin: false,
  // "yes" | "no" | "unknown" — "unknown" means the probe hasn't landed yet. Switchers use
  // this to render a disabled placeholder rather than vanishing.
  adminStatus: "unknown",
  adminProbeFailed: false,
  callerEmail: "",
  orgs: [],
  orgsFailed: false,
  actingOrgId: null,
  actingOrg: null,
  // Per-user "view as" (analytics). Nested under the acting org.
  orgUsers: [],
  actingUserId: null,
  actingUser: null,
  isImpersonating: false,
  viewAsReadOnly: false,
  setActingOrg: () => {},
  setActingUser: () => {},
  clearActingUser: () => {},
  clearActing: () => {},
  refreshOrgs: () => {},
  retryAdminProbe: () => {},
});

const PROBE_RETRIES = 3;
const PROBE_BACKOFF_MS = [600, 1500, 3500];

export function ImpersonationProvider({ children }) {
  const { inProgress } = useMsal();
  const isAuth = useIsAuthenticated();

  // Seeded from the per-browser hint so an admin's controls are present on first paint.
  const [adminStatus, setAdminStatus] = useState(readAdminHint);
  const [adminProbeFailed, setAdminProbeFailed] = useState(false);
  const [callerEmail, setCallerEmail] = useState("");
  const [orgs, setOrgs] = useState([]);
  const [orgsFailed, setOrgsFailed] = useState(false);
  const [actingOrgId, setActingOrgIdState] = useState(() => getActingOrgId());
  const [orgUsers, setOrgUsers] = useState([]);
  const [actingUserId, setActingUserIdState] = useState(() => getActingUserId());
  const [probeNonce, setProbeNonce] = useState(0);
  const [probeConfirmed, setProbeConfirmed] = useState(false);

  // Deliberately NOT seeded from the hint. `adminStatus` (hint-seeded) governs only whether the
  // impersonation switcher is on screen — an inert menu whose every entry is authorised
  // server-side. `isAdmin` gates staff-only UI that reveals already-fetched fields
  // (ProjectDetail internals, the ModelChat details toggle, /admin/usage), so it stays strictly
  // probe-derived: a hand-edited localStorage value must not unlock it.
  const isAdmin = adminStatus === "yes" && probeConfirmed;

  const refreshOrgs = useCallback(async () => {
    try {
      const data = await listAdminOrgs();
      setOrgs(data.orgs || []);
      setOrgsFailed(false);
    } catch (e) {
      // Keep whatever we already had. Emptying the list on a transient failure is what made
      // the acting-org label and the banner disappear while the impersonation headers were
      // still being sent on every request.
      setOrgsFailed(true);
    }
  }, []);

  // Load the acting org's users (for the per-user "view as" picker). Normalizes the
  // response shape ({users:[...]} or a bare array). Non-fatal on failure.
  const refreshOrgUsers = useCallback(async (orgId) => {
    if (!orgId) { setOrgUsers([]); return; }
    try {
      const data = await listOrgUsers(orgId);
      const arr = Array.isArray(data) ? data : (data.users || []);
      setOrgUsers(arr);
    } catch (e) {
      // Leave the list as-is; viewAsReadOnly stays conservative when the record is missing.
    }
  }, []);

  const retryAdminProbe = useCallback(() => setProbeNonce((n) => n + 1), []);

  // Admin probe. Two things the old version got wrong and this fixes:
  //
  //  1. It fired immediately at mount, racing MSAL's own bookkeeping. This provider sits
  //     ABOVE the router (index.js), outside RequireAuth, so it ran before MSAL had finished
  //     restoring the session — losing that race set isAdmin=false for the whole page load,
  //     which is why view-as "disappeared" and only came back after N refreshes. Now it waits
  //     for inProgress === None and an authenticated account.
  //  2. A single failure (timeout, blip, cold start) was final. Now transient failures retry
  //     with backoff and NEVER downgrade a known-admin to non-admin — only an authoritative
  //     is_admin:false response does that.
  useEffect(() => {
    if (inProgress !== InteractionStatus.None) return;   // MSAL still settling
    if (!isAuth) return;                                  // nothing to probe as

    let cancelled = false;

    (async () => {
      for (let attempt = 0; attempt <= PROBE_RETRIES; attempt++) {
        try {
          const me = await getAdminMe();
          if (cancelled) return;
          const verdict = me.is_admin ? "yes" : "no";
          setAdminStatus(verdict);
          setProbeConfirmed(true);
          setAdminProbeFailed(false);
          writeAdminHint(verdict);
          setCallerEmail(me.email || "");
          if (me.is_admin) {
            await refreshOrgs();
            const org = getActingOrgId();
            if (org) await refreshOrgUsers(org);
          } else {
            // Authoritative non-admin: scrub any impersonation state so a normal user's
            // session can never carry an acting header. (Also covers an admin whose access
            // was revoked while their browser still held the hint.)
            if (getActingOrgId()) { setActingOrgId(null); setActingOrgIdState(null); }
            if (getActingUserId()) { setActingUserId(null); setActingUserIdState(null); }
            setOrgs([]);
            setOrgUsers([]);
          }
          return;
        } catch (e) {
          if (cancelled) return;
          if (attempt < PROBE_RETRIES) {
            await new Promise((r) => setTimeout(r, PROBE_BACKOFF_MS[attempt]));
            if (cancelled) return;
            continue;
          }
          // Out of retries. Hold the previous/hinted verdict rather than claiming
          // "not an admin" — the switcher renders disabled with a retry instead of vanishing.
          setAdminProbeFailed(true);
          setCallerEmail("");
        }
      }
    })();

    return () => { cancelled = true; };
  }, [inProgress, isAuth, probeNonce, refreshOrgs, refreshOrgUsers]);

  const setActingOrg = useCallback((orgId) => {
    setActingOrgId(orgId);          // updates api.js module var + localStorage (also clears acting-user)
    setActingOrgIdState(orgId);
    setActingUserIdState(null);     // switching org drops any per-user selection
    refreshOrgUsers(orgId);
  }, [refreshOrgUsers]);

  const setActingUser = useCallback((user) => {
    const uid = user && (user.user_id || user.uid || user.email);
    setActingUserId(uid || null);
    setActingUserIdState(uid || null);
  }, []);

  const clearActingUser = useCallback(() => {
    setActingUserId(null);
    setActingUserIdState(null);
  }, []);

  const clearActing = useCallback(() => {
    setActingOrgId(null);           // also clears acting-user in api.js
    setActingOrgIdState(null);
    setActingUserIdState(null);
    setOrgUsers([]);
  }, []);

  const actingOrg = actingOrgId ? orgs.find((o) => o.org_id === actingOrgId) || null : null;
  const actingUser = actingUserId
    ? orgUsers.find((u) => (u.user_id || u.uid || u.email) === actingUserId) || null
    : null;
  // Keyed off the acting id ALONE, not off finding it in the orgs list. api.js sends
  // X-Acting-Org/X-Acting-User from localStorage on every request, so the banner has to be
  // visible whenever those ids are set — otherwise a failed org-list fetch hides the banner
  // while you are still silently looking at a customer's data.
  const isImpersonating = !!actingOrgId;
  // Read-only when viewing as a specific USER whose org is NOT a flagged test
  // account. Defaults to read-only while the user record is still loading
  // (safer to disable writes than to let one through and 403). Test accounts
  // and plain org-level impersonation are NOT read-only.
  const viewAsReadOnly = !!(
    actingUserId && !(actingUser && (actingUser.is_test || actingUser.is_test_user))
  );

  return (
    <ImpersonationContext.Provider
      value={{
        isAdmin,
        adminStatus,
        adminProbeFailed,
        callerEmail,
        orgs,
        orgsFailed,
        actingOrgId,
        actingOrg,
        orgUsers,
        actingUserId,
        actingUser,
        isImpersonating,
        viewAsReadOnly,
        setActingOrg,
        setActingUser,
        clearActingUser,
        clearActing,
        refreshOrgs,
        retryAdminProbe,
      }}
    >
      {children}
    </ImpersonationContext.Provider>
  );
}

export function useImpersonation() {
  return useContext(ImpersonationContext);
}
